// CRecordsScene — 我的紀錄：稱號、累積統計、最佳紀錄與牌型圖鑑
// 對應 docs/PRD-Freemium.md 第 8.3 節
//
// 資料來自伺服器帳號快照（進場時重新同步）；圖鑑胡過的台型點亮並顯示次數。
//
// === 使用圖片 ===
// 無直接圖片 — 桌面由 createTableBackground（Phaser.Graphics）繪製；面板由 page-skin 繪製；
// 右上 X：close-button（純 Graphics）；圖鑑格子為純程式繪製

import Phaser from 'phaser';
import { BGMTrack } from '@audio/audio-keys';
import { playUIClick, syncBackgroundMusic } from '@audio/scene-audio';
import { LOGICAL_HEIGHT, LOGICAL_WIDTH } from '@config/game-config';
import { ZH_TW } from '@i18n/index';
import { accountSnapshot, refreshAccount } from '@net/account-client';
import type { IPlayerStats } from '@net/account-service';
import { currentTitle, nextTitle } from '@net/titles';
import { createCloseButton, CloseButtonLayout } from '@ui/close-button';
import { noticeFromError } from '@ui/notice-toast';
import { createPagePanel, createPageTitle, PAGE_THEME } from '@ui/page-skin';
import { createTableBackground } from '@ui/table-background';
import { SceneKey } from './scene-keys';

// ---- 版面常數 ----
const PANEL_X = LOGICAL_WIDTH / 2;
const PANEL_Y = LOGICAL_HEIGHT / 2;
const PANEL_WIDTH = 1180;
const PANEL_HEIGHT = 660;
const TITLE_Y = 62;
const TITLE_LABEL_Y = 126; // 目前稱號
const TITLE_LABEL_FONT = 36;
const NEXT_Y = 168;
const STATS_Y = 206;
const SMALL_FONT = 22;
const GRID_TOP = 250; // 圖鑑第一列中心
const GRID_COLUMNS = 6;
const CELL_WIDTH = 176;
const CELL_HEIGHT = 50;
const CELL_GAP_X = 10;
const CELL_GAP_Y = 10;
const CELL_RADIUS = 10;
const CELL_FONT = 20;
const CELL_LIT = 0xd9a441; // 胡過：金色
const CELL_DIM = 0x1d4a36; // 還沒胡過
const CELL_STROKE = 0xe7c26b;

/** 圖鑑收錄的台型（不含莊家、連莊）。 */
const COLLECTIBLE_TAI = Object.keys(ZH_TW.scoring).filter(
  (key) => key !== 'dealer' && key !== 'lianzhuang'
);

export class CRecordsScene extends Phaser.Scene {
  private content: Phaser.GameObjects.GameObject[] = [];

  constructor() {
    super({ key: SceneKey.Records });
  }

  create(): void {
    this.content = [];
    syncBackgroundMusic(this, BGMTrack.Lobby);
    createTableBackground(this);
    createPagePanel(this, PANEL_X, PANEL_Y, PANEL_WIDTH, PANEL_HEIGHT);
    createPageTitle(this, PANEL_X, TITLE_Y, '我的紀錄');
    createCloseButton(
      this,
      PANEL_X + PANEL_WIDTH / 2 - CloseButtonLayout.insetX,
      PANEL_Y - PANEL_HEIGHT / 2 + CloseButtonLayout.insetY,
      () => this.goBack()
    );
    this.input.keyboard?.on('keydown-ESC', () => this.goBack());
    const cached = accountSnapshot();
    if (cached) this.renderStats(cached.stats);
    refreshAccount()
      .then((snapshot) => {
        if (this.scene.isActive()) this.renderStats(snapshot.stats);
      })
      .catch((error: unknown) => noticeFromError(error));
  }

  private renderStats(stats: IPlayerStats): void {
    for (const object of this.content) object.destroy();
    this.content = [];
    const title = currentTitle(stats);
    const next = nextTitle(stats);
    this.text(PANEL_X, TITLE_LABEL_Y, `稱號：${title.name}`, TITLE_LABEL_FONT, '#ffe9a8', true);
    if (next) {
      const [now, target] = next.progress(stats);
      this.text(
        PANEL_X,
        NEXT_Y,
        `下一個稱號「${next.name}」：${next.requirement}（${now}／${target}）`,
        SMALL_FONT,
        PAGE_THEME.muted
      );
    }
    const best = stats.bestHand
      ? `最佳：${stats.bestHand.tai} 台（${stats.bestHand.items
          .slice(0, 3)
          .map((key) => this.taiName(key))
          .join('、')}）`
      : '最佳：還沒胡過牌';
    this.text(
      PANEL_X,
      STATS_Y,
      `打了 ${stats.rounds} 手・胡牌 ${stats.wins} 次・自摸 ${stats.selfDraws} 次・${best}`,
      SMALL_FONT
    );
    const gridWidth = GRID_COLUMNS * CELL_WIDTH + (GRID_COLUMNS - 1) * CELL_GAP_X;
    const left = PANEL_X - gridWidth / 2 + CELL_WIDTH / 2;
    COLLECTIBLE_TAI.forEach((key, index) => {
      const x = left + (index % GRID_COLUMNS) * (CELL_WIDTH + CELL_GAP_X);
      const y = GRID_TOP + Math.floor(index / GRID_COLUMNS) * (CELL_HEIGHT + CELL_GAP_Y) + 30;
      const count = stats.taiTypes[key] ?? 0;
      const g = this.add.graphics();
      g.fillStyle(count > 0 ? CELL_LIT : CELL_DIM, 1);
      g.fillRoundedRect(
        x - CELL_WIDTH / 2,
        y - CELL_HEIGHT / 2,
        CELL_WIDTH,
        CELL_HEIGHT,
        CELL_RADIUS
      );
      if (count > 0) {
        g.lineStyle(2, CELL_STROKE);
        g.strokeRoundedRect(
          x - CELL_WIDTH / 2,
          y - CELL_HEIGHT / 2,
          CELL_WIDTH,
          CELL_HEIGHT,
          CELL_RADIUS
        );
      }
      this.content.push(g);
      this.text(
        x,
        y,
        count > 0 ? `${this.taiName(key)} ×${count}` : this.taiName(key),
        CELL_FONT,
        count > 0 ? '#3a2006' : '#7f9d8b',
        count > 0
      );
    });
  }

  private taiName(key: string): string {
    return (ZH_TW.scoring as Record<string, string>)[key] ?? key;
  }

  private text(
    x: number,
    y: number,
    content: string,
    size: number,
    color: string = PAGE_THEME.text,
    bold = false
  ): void {
    this.content.push(
      this.add
        .text(x, y, content, {
          color,
          fontFamily: PAGE_THEME.font,
          fontSize: `${size}px`,
          fontStyle: bold ? 'bold' : 'normal',
        })
        .setOrigin(0.5)
    );
  }

  private goBack(): void {
    playUIClick(this);
    this.scene.start(SceneKey.Home);
  }
}
